/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2021. All rights reserved
------------------------------------------------------------------------------------------------- */

import { css, html, LitElement } from 'lit-element';
import { dispatchCustomEvent } from '../../utils/custom-event-dispatcher.js';
import { getDecimalNumberPrecision } from '../../utils/decimal-number-precision.js';

/**
 * @prop {String} parentElement - Defines the parent object for arrow-controls.
 * @prop {Number} value - Defines the current value of the parent component. Must be a number between the min and max values.
 * @prop {Number} min - Default 0. Defines the minimum value allowed.
 * @prop {Number} max - Default 100. Defines the maximum value allowed.
 * @prop {Number} step - Default 1. Defines the intervals of the parent component. If set to 0.1 for example, decimals will be allowed.
 * @prop {Boolean} horizontal - Defines the type of arrow-icons. For eg: left-s or up-s
 * @prop {Boolean} disabled - If set to `true`, user interaction won't be allowed and the styles of the component will change.
 * @fires value-change-end - Marks the end of a long press on the arrow keys as well as drag end.
 * @fires range-value-change-end - Marks the end of the whole range slider drag.*/
export class SHArrowControls extends LitElement {
  static get properties() {
    return {
      parentElement: { type: String },
      value: { type: Number },
      min: { type: Number },
      max: { type: Number },
      step: { type: Number },
      horizontal: { type: Boolean },
      disabled: { type: Boolean },
    };
  }

  static get styles() {
    return [
      css`
        :host {
          display: flex;
        }
        :host([disabled]) #plus,
        :host([disabled]) #minus {
          color: rgb(var(--ui-1));
        }
      `,
    ];
  }

  render() {
    return html`
      <span>
        <sh-icon
          id="minus"
          button
          icon="${this.horizontal ? 'left-s' : 'up-s'}"
          ?disabled="${this.disabled}"
          @mousedown="${this._startCounter}"
          @mousemove="${this._releaseCounter}"
          @mouseup="${this._releaseCounter}"
          @touchstart="${this._startCounter}"
          @touchend="${this._releaseCounter}"
        ></sh-icon>
      </span>
      <span>
        <sh-icon
          id="plus"
          button
          icon="${this.horizontal ? 'right-s' : 'down-s'}"
          ?disabled="${this.disabled}"
          @mousedown="${this._startCounter}"
          @mousemove="${this._releaseCounter}"
          @mouseup="${this._releaseCounter}"
          @touchstart="${this._startCounter}"
          @touchend="${this._releaseCounter}"
        ></sh-icon>
      </span>
    `;
  }

  constructor() {
    super();
    this.min = 0;
    this.max = 100;
    this.step = 1;
  }

  _startCounter(e) {
    if (e.touches) {
      this.skipMouseMove = true;
    }
    e.target.style.transition = '0s all linear';
    this._releaseCounter();
    this._setFlags(e);
    this.clock = window.requestAnimationFrame(this._clockControl.bind(this));
  }

  _handleOnClick() {
    if (this._mouseDownAddition) {
      this._mouseDownAddition = false;
      return;
    }
    if (this._clickedOnPlusIcon) {
      this._handlePlusCalc();
    } else if (this._clickedOnMinusIcon) {
      this._handleMinusCalc();
    }
  }

  _clockControl() {
    this.timer = setTimeout(this._handleOnMouseDown.bind(this), 100);
  }

  _handleOnMouseDown() {
    if (this._clickedOnPlusIcon) {
      this._handlePlusCalc();
    } else if (this._clickedOnMinusIcon) {
      this._handleMinusCalc();
    }
    this._mouseDownAddition = true;
    this.clock = window.requestAnimationFrame(this._clockControl.bind(this));
  }

  _handleMinusCalc() {
    const decimalPoints = this.decimalNumberPrecision();
    let tempval = 0;
    if (this.value !== this.min) {
      tempval = parseFloat(this.value) - parseFloat(this.step);
      tempval = Number(tempval).toFixed(decimalPoints);
      if (tempval >= this.min) {
        this.value = Number(tempval);
        dispatchCustomEvent(this, 'control-value-change');
      }
    }
  }

  _handlePlusCalc() {
    const decimalPoints = this.decimalNumberPrecision();
    let tempval = 0;
    if (this.value !== this.max) {
      tempval = parseFloat(this.value) + parseFloat(this.step);
      tempval = Number(tempval).toFixed(decimalPoints);
      if (tempval <= this.max) {
        this.value = Number(tempval);
        dispatchCustomEvent(this, 'control-value-change');
      }
    }
  }

  decimalNumberPrecision() {
    let stepInString, minInString;
    stepInString = this.step.toString();
    minInString = this.min.toString();
    let numberOfDecimalPoints;
    numberOfDecimalPoints = 0;
    if (stepInString.includes('.')) {
      numberOfDecimalPoints = getDecimalNumberPrecision(stepInString);
    } else if (minInString.includes('.')) {
      numberOfDecimalPoints = getDecimalNumberPrecision(minInString);
    }
    return numberOfDecimalPoints;
  }

  _releaseCounter(e = null) {
    /**
     * The below check for mousemove was to prevent mousemove from clearing the timer
     * for repeated addition/subtraction in touch scenarios.
     * In some touch scenarios, the mousemove was coming after touchstart and before touchend.
     * So to prevent this scenario we set a flag when we detect mousemove.
     * Also event.preventDefault() has to be called after touchend actions are done,
     * to prevent the subsequent mouse event handlers from executing.
     */

    if (e && e.type === 'mousemove' && this.skipMouseMove) {
      this.skipMouseMove = false;
      return;
    }
    window.cancelAnimationFrame(this.clock);
    clearTimeout(this.timer);
    if (e && (e.type === 'mouseup' || e.type === 'touchend')) {
      dispatchCustomEvent(this, 'control-value-change-end');
    }
    if (e && e.type !== 'mousemove') {
      this._setFlags(e);
      this._handleOnClick();
    }
    if (e && e.touches) {
      e.preventDefault();
    }
    this._clickedOnPlusIcon = false;
    this._clickedOnMinusIcon = false;
  }

  _setFlags(e) {
    /**
     * sets the flag for the respective operation.
     */
    if (e.target.id === 'plus') {
      this._clickedOnPlusIcon = true;
    } else if (e.target.id === 'minus') {
      this._clickedOnMinusIcon = true;
    }
  }

  disconnectedCallback() {
    window.cancelAnimationFrame(this.clock);
    clearTimeout(this.timer);
    super.disconnectedCallback();
  }
}
customElements.define('arrow-controls', SHArrowControls);
